import { useAppDispatch, useAppSelector } from "../../App/hooks";
import Button from "../../Components/Button";
import TabBox from "../../Components/Tabbox/TabBox";
import {
  setStepEditRowValues,
  setStepOrderTableFetchDataFlag,
} from "../../Features/Step/stepSlice";
import {
  IEditStepOrderFormValues,
  IFailedStepOrderEditItem,
  IStepEditVolumeContext,
  IStepEditVolumePreview,
  StepOrderSide,
  VolumeCalculationType,
} from "../../Interfaces/stepInterfaces";
import { useState, useEffect } from "react";
import TextInput from "../../Components/TextInput";
import { addSeperatorToInput } from "../../helper";
import {
  editStepOrder,
  getStepEditVolumeContext,
  getSimilarStepOrders,
  previewStepEditVolume,
} from "../../Requests/stepRequests";
import { toast } from "react-toastify";
import DynamicMultiSelectDropdown from "../../Components/Dropdown/DynamicMultiSelectDropdown";
import FullScreenLoading from "../../Components/Loading/FullScreenLoading";
import Modal from "../../Components/Modal/Modal";
import TimePicker from "../../Components/TimePicker";
import CheckBox from "../../Components/Checkbox";
import StaticDropdown from "../../Components/Dropdown/StaticDropdown";
import { useDebouncer } from "src/Hooks/useDebouncer";
import {
  hasInvalidStepOrderTimeRange,
  STEP_ORDER_TIME_RANGE_ERROR,
} from "./stepOrderScheduleValidation";

const getTimePickerValue = (value?: string | null) =>
  value ? `${new Date().toISOString().slice(0, 10)}T${value}` : null;

const normalizeScheduleTime = (value?: string | null) =>
  !value || value === "-" || value === "آنی" ? null : value;

const EditOrder = () => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [editWarning, setEditWarning] = useState<string | null>(null);
  const [scheduleTouched, setScheduleTouched] = useState(false);
  const [isImmediateStart, setIsImmediateStart] = useState(true);
  const [volumeContext, setVolumeContext] = useState<IStepEditVolumeContext | null>(null);
  const [volumeContextFailed, setVolumeContextFailed] = useState(false);
  const [volumeContextRetry, setVolumeContextRetry] = useState(0);
  const [volumeTouched, setVolumeTouched] = useState(false);
  const [volumePreview, setVolumePreview] = useState<IStepEditVolumePreview | null>(null);
  const [volumePreviewLoading, setVolumePreviewLoading] = useState(false);
  const [failureSummary, setFailureSummary] = useState<string | null>(null);
  const [failedOrders, setFailedOrders] = useState<IFailedStepOrderEditItem[] | null>(null);
  const [failedEditedCount, setFailedEditedCount] = useState<number | null>(null);

  const editOrderRowValues = useAppSelector(
    (store) => store.step.editOrderRowValues
  );
  const volumeContextReady = volumeContext != null &&
    volumeContext.stepOrderId === editOrderRowValues?.id;

  const getDirection = (direction: string) => {
    if (direction === "خرید") return StepOrderSide.Buy;
    return StepOrderSide.Sell;
  };

  const [formValues, setFormValues] = useState<IEditStepOrderFormValues>(
    {} as IEditStepOrderFormValues
  );

  useEffect(() => {
    if (editOrderRowValues) {
      setEditWarning(null);
      setFailedOrders(null);
      setFailureSummary(null);
      setFailedEditedCount(null);
      setVolumeContext(null);
      setVolumeContextFailed(false);
      setVolumeTouched(false);
      setVolumePreview(null);
      setFormValues({
        applyToAll: null,
        blockSize: null,
        fromPrice: editOrderRowValues.firstStep,
        instrumentId: editOrderRowValues.instrument,
        remainedVolume: null,
        stepCount: editOrderRowValues.stepCount,
        stepOrderId: editOrderRowValues.id,
        stepOrderIds: null,
        toPrice: editOrderRowValues.lastStep,
        direction: getDirection(editOrderRowValues.orderSideStr),
        schedule: {
          startTime: normalizeScheduleTime(
            editOrderRowValues.scheduledStartTimeStr,
          ),
          stopTime: normalizeScheduleTime(
            editOrderRowValues.scheduledStopTimeStr,
          ),
        },
      });
      setScheduleTouched(false);
      setIsImmediateStart(
        normalizeScheduleTime(editOrderRowValues.scheduledStartTimeStr) ===
          null,
      );
    }
  }, [editOrderRowValues?.id]);

  useEffect(() => {
    if (!editOrderRowValues) return;
    let cancelled = false;
    setVolumeContext(null);
    setVolumeContextFailed(false);
    getStepEditVolumeContext(editOrderRowValues.id)
      .then((context) => {
        if (cancelled) return;
        if (context.stepOrderId !== editOrderRowValues.id) {
          setVolumeContextFailed(true);
          return;
        }
        setVolumeContext(context);
        setFormValues((prev) => ({ ...prev,
          volumeCalculationType: context.volumeCalculationType,
          volumeCalculationValue: context.volumeCalculationValue,
        }));
      })
      .catch(() => { if (!cancelled) setVolumeContextFailed(true); });
    return () => { cancelled = true; };
  }, [editOrderRowValues?.id, volumeContextRetry]);

  const changeHandler = (name: string, value: any) => {
    if (name === "applyToAll" || name === "stepOrderIds") setEditWarning(null);
    if (name === "volumeCalculationType" || name === "volumeCalculationValue")
      setVolumeTouched(true);
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const debouncedType = useDebouncer(formValues.volumeCalculationType, 500);
  const debouncedValue = useDebouncer(formValues.volumeCalculationValue, 500);
  const debouncedFromPrice = useDebouncer(formValues.fromPrice, 500);
  const debouncedToPrice = useDebouncer(formValues.toPrice, 500);

  useEffect(() => {
    // A fixed amount needs no server calculation; its effect is shown from the context.
    if (!volumeTouched || !editOrderRowValues || debouncedType == null
      || debouncedType === VolumeCalculationType.FixedAmount
      || Number(debouncedValue) <= 0) {
      setVolumePreview(null);
      setVolumePreviewLoading(false);
      return;
    }
    let cancelled = false;
    setVolumePreviewLoading(true);
    previewStepEditVolume({
      stepOrderId: editOrderRowValues.id,
      volumeCalculationType: debouncedType,
      volumeCalculationValue: Number(debouncedValue),
      fromPrice: Number(debouncedFromPrice) || editOrderRowValues.firstStep,
      toPrice: Number(debouncedToPrice) || editOrderRowValues.lastStep,
    }).then((result) => { if (!cancelled) setVolumePreview(result); })
      .catch(() => { if (!cancelled) setVolumePreview({ success: false, reason: "پیش‌نمایش ناموفق بود" }); })
      .finally(() => { if (!cancelled) setVolumePreviewLoading(false); });
    return () => { cancelled = true; };
  }, [volumeTouched, debouncedType, debouncedValue,
    debouncedFromPrice, debouncedToPrice, editOrderRowValues?.id]);

  useEffect(() => {
    if (!formValues.applyToAll) changeHandler("stepOrderIds", null);
  }, [formValues.applyToAll]);

  const onSendClick = () => {
    if (loading || (volumeTouched && !volumeContextReady)) return;
    setEditWarning(null);
    if (
      scheduleTouched &&
      hasInvalidStepOrderTimeRange(formValues.schedule, isImmediateStart)
    ) {
      toast.error(STEP_ORDER_TIME_RANGE_ERROR);
      return;
    }

    setLoading(true);
    // These fields are commands, not a snapshot: null means "keep the current volume".
    // Compare per order on the server; the opened order is not representative of a group.
    const requestedValue = Number(formValues.volumeCalculationValue);
    editStepOrder({
      ...formValues,
      remainedVolume: volumeTouched ? null : formValues.remainedVolume,
      volumeCalculationType: volumeTouched ? formValues.volumeCalculationType : null,
      volumeCalculationValue: volumeTouched && requestedValue > 0 ? requestedValue : null,
      schedule: scheduleTouched ? formValues.schedule : undefined,
      startImmediately: scheduleTouched ? isImmediateStart : undefined,
    })
      .then((resp) => {
        if (resp?.failedOrders?.length) {
          dispatch(setStepOrderTableFetchDataFlag(true));
          if (!resp.summary) {
            setEditWarning(resp.failedOrders[0].reason || resp.msg);
            return;
          }
          setFailureSummary(resp.msg);
          setFailedOrders(resp.failedOrders);
          setFailedEditedCount(resp.summary?.editedCount ?? null);
          return;
        }
        if (resp?.data !== true) {
          setEditWarning(resp?.msg || "ویرایش انجام نشد؛ وضعیت سفارش را بررسی کنید.");
          return;
        }
        dispatch(setStepOrderTableFetchDataFlag(true));
        if (resp?.summary) {
          dispatch(setStepEditRowValues(null));
          if (
            resp.data !== true ||
            resp.summary.skippedUnresolvedCount > 0 ||
            resp.summary.noRemainingVolumeCount > 0
          ) {
            toast.warning(resp.msg);
          } else {
            toast.success(resp.msg);
          }
          return;
        }
        toast.success(resp.msg);
        dispatch(setStepEditRowValues(null));
      })
      .catch((e) =>
        setEditWarning(
          e?.response?.data?.value ||
            e?.response?.data?.msg ||
            "پاسخ ویرایش دریافت نشد؛ پیش از تلاش مجدد، وضعیت سفارش را بررسی کنید.",
        ),
      )
      .finally(() => setLoading(false));
  };

  const checkIsBtnDisabled = () => {
    if (!formValues.fromPrice || !formValues.toPrice || !formValues.stepCount)
      return true;
    if (volumeTouched && !volumeContextReady) return true;
    if (volumeTouched && (!formValues.volumeCalculationType ||
      Number(formValues.volumeCalculationValue) <= 0)) return true;
    return false;
  };

  const onCancelClick = () => {
    dispatch(setStepEditRowValues(null));
  };

  const getTabBoxChildren = () => [
    {
      title: editOrderRowValues?.sahraName || "نام اکانت",
      index: 1,
      value: false,
    },
    {
      title: "همه سفارشات مشابه",
      index: 2,
      value: true,
    },
  ];

  const getTabBoxActiveTab = () => {
    if (formValues.applyToAll) return 2;
    return 1;
  };

  const getOrderSideClassname = (orderSide: string) => {
    switch (orderSide) {
      case "خرید":
        return "BuyCssClass";
      case "فروش":
        return "SellCssClass";
      default:
        break;
    }
  };

  const volumeMethods = [
    { id: VolumeCalculationType.FixedAmount, name: "مقدار ثابت" },
    { id: VolumeCalculationType.PortfolioPercent, name: "% از پورتفو" },
    { id: VolumeCalculationType.AssetPercent, name: "% از دارایی" },
    { id: VolumeCalculationType.TargetPortfolioPercent, name: "% هدف از پورتفو" },
    { id: VolumeCalculationType.BuyingPowerPercent, name: "% از قدرت خرید" },
    { id: VolumeCalculationType.FixedCapitalAmount, name: "سرمایه درگیر (مبلغ) ثابت" },
  ];

  const volumeValueLabel =
    volumeMethods.find((item) => item.id === formValues.volumeCalculationType)
      ?.name ?? "مقدار روش تعیین حجم";
  const isFixedAmountVolume =
    formValues.volumeCalculationType === VolumeCalculationType.FixedAmount;
  const isGroupEdit = !!formValues.applyToAll;
  const fixedAmountTargetBelowFill =
    Number(formValues.volumeCalculationValue) < (volumeContext?.cumulativeFilledVolume ?? 0);
  const fixedAmountRemaining = Math.max(0, Number(formValues.volumeCalculationValue)
    - (volumeContext?.cumulativeFilledVolume ?? 0) - (volumeContext?.cumulativeExcludedVolume ?? 0));

  const onFailedOrdersClose = () => {
    setFailedOrders(null);
    setFailedEditedCount(null);
    dispatch(setStepEditRowValues(null));
  };

  if (!editOrderRowValues) return null;

  const formUI = (
    <div className="flex flex-col justify-start items-start gap-[6px] whitespace-nowrap text-xs">
      {loading ? <FullScreenLoading /> : <></>}
      <div className="flex flex-row justify-between items-center w-full">
        <div className="text-sm font-bold">ویرایش</div>
        <div className="flex flex-row justify-start items-center gap-3">
          <div>نماد:</div>
          <div>{editOrderRowValues.namad}</div>
        </div>
        <div className="flex flex-row justify-start items-center gap-3">
          <div>جهت سفارش:</div>
          <div
            className={getOrderSideClassname(editOrderRowValues.orderSideStr)}
          >
            {editOrderRowValues.orderSideStr}
          </div>
        </div>
        <div className="flex flex-row justify-start items-center gap-1 text-xs">
          <div className="ltr">موجودی پورتفو:</div>
          <div>{editOrderRowValues.portfoAmount}</div>
        </div>
      </div>
      <div className="flex flex-row w-full justify-between items-center gap-3">
        <TabBox
          children={getTabBoxChildren()}
          activeTab={getTabBoxActiveTab()}
          onChange={(name, _, value) => changeHandler(name, value)}
          name="applyToAll"
        />
      </div>
      <div className="flex flex-row w-full justify-between items-center gap-3">
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          {formValues.applyToAll ? (
            <DynamicMultiSelectDropdown
              dropdown={{
                name: "stepOrderIds",
                placeholder: "همه سفارشات مشابه",
              }}
              getData={() => getSimilarStepOrders(formValues.stepOrderId)}
              onChange={changeHandler}
              value={formValues.stepOrderIds || []}
            />
          ) : null}
        </div>

        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>درصد بلوک:</div>
            <div className={`${formValues.applyToAll ? "line-through" : null}`}>
              {addSeperatorToInput(editOrderRowValues.blockSize, "priceWithDecimal")}
            </div>
          </div>
          <TextInput
            label=""
            name="blockSize"
            onChange={changeHandler}
            value={formValues.blockSize?.toString()}
            kind="price"
          />
        </div>
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        {/* <div className="flex flex-row justify-start items-center gap-3">
          <div>موجودی پورتفوی:</div>
           <div>{editOrderRowValues.}</div>
        </div> */}
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>ابتدای بازه:</div>
            <div>
              {addSeperatorToInput(editOrderRowValues.firstStep, "price")}
            </div>
          </div>
          <TextInput
            label=""
            name="fromPrice"
            onChange={changeHandler}
            value={formValues.fromPrice?.toString()}
            kind="price"
          />
        </div>
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>انتهای بازه:</div>
            <div>
              {addSeperatorToInput(editOrderRowValues.lastStep, "price")}
            </div>
          </div>
          <TextInput
            label=""
            name="toPrice"
            onChange={changeHandler}
            value={formValues.toPrice?.toString()}
            kind="price"
          />
        </div>
      </div>
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row justify-start items-center gap-3 w-1/2">
          <div className="flex flex-row justify-start items-center gap-1 w-[90%] text-xs">
            <div>پلکان:</div>
            <div>{editOrderRowValues.stepCount}</div>
          </div>
          <TextInput
            label=""
            name="stepCount"
            onChange={changeHandler}
            value={formValues.stepCount?.toString()}
            kind="price"
          />
        </div>
      </div>
      {volumeContextReady ? (
        <div className="w-full text-xs">
          هدف کل: {addSeperatorToInput(volumeContext.targetTotalVolume, "price")}
          {" | "}انجام‌شده: {addSeperatorToInput(volumeContext.cumulativeFilledVolume, "price")}
          {!!volumeContext.cumulativeExcludedVolume && <>
            {" | "}کنارگذاشته‌شده به‌علت ابهام: {addSeperatorToInput(volumeContext.cumulativeExcludedVolume, "price")}
          </>}
          {" | "}باقی‌مانده: {addSeperatorToInput(volumeContext.remainingVolume, "price")}
        </div>
      ) : (
        <div className="w-full text-xs">
          {volumeContextFailed
            ? "اطلاعات حجم در دسترس نیست؛ ویرایش بدون تغییر حجم همچنان ممکن است."
            : "در حال دریافت اطلاعات حجم..."}
          {volumeContextFailed && (
            <Button
              onClick={() => setVolumeContextRetry((retry) => retry + 1)}
              text="تلاش مجدد"
              variant="outlined"
              className="!w-20 !h-6 !text-xs"
            />
          )}
        </div>
      )}
      <div className="w-full flex flex-row justify-between items-center gap-3">
        <StaticDropdown
          dropdown={{ name: "volumeCalculationType", placeholder: "روش تعیین حجم", disabled: !volumeContextReady }}
          onChange={changeHandler}
          value={formValues.volumeCalculationType ?? undefined}
          options={volumeMethods}
        />
        <TextInput
          label={volumeValueLabel}
          name="volumeCalculationValue"
          onChange={changeHandler}
          value={formValues.volumeCalculationValue?.toString() ?? ""}
          kind="priceWithDecimal"
          disabled={!volumeContextReady}
        />
      </div>
      {volumeTouched ? (
        <div className="w-full text-xs text-gray-500">
          {isFixedAmountVolume
            ? Number(formValues.volumeCalculationValue) <= 0
              ? null
              : volumeContextReady && !isGroupEdit
              ? fixedAmountTargetBelowFill
                ? <span className="text-red-500">هدف کل نمی‌تواند از حجم انجام‌شده کمتر باشد</span>
                : `حجم جایگزین: ${addSeperatorToInput(fixedAmountRemaining, "price") ?? "0"}`
              : "مقدار ثابت، هدف کل هر سفارش است؛ حجم انجام‌شده از آن کم می‌شود."
            : volumePreviewLoading ? "در حال محاسبه..."
              : volumePreview?.success
                ? `حجم جایگزین${isGroupEdit ? " (فقط برای این حساب)" : ""}: ${addSeperatorToInput(volumePreview.remainingVolume ?? 0, "price")}`
                : volumePreview?.reason ?? null}
        </div>
      ) : null}
      <div className="flex flex-col justify-start items-start gap-[6px] w-full">
        <div className="flex w-full justify-start">
          <CheckBox
            checked={isImmediateStart}
            label="شروع آنی"
            name="isImmediateStart"
            onChange={(_, checked) => {
              setIsImmediateStart(checked);
              setScheduleTouched(true);
              if (!checked) return;

              const schedule = {
                ...(formValues.schedule || {
                  startTime: null,
                  stopTime: null,
                }),
                startTime: null,
              };
              changeHandler("schedule", schedule);
            }}
          />
        </div>
        <div className="flex flex-row justify-between items-start gap-3 w-full">
          {!isImmediateStart ? (
            <div className="w-1/2">
              <TimePicker
                name="startTime"
                placeholder="زمان شروع"
                value={getTimePickerValue(formValues.schedule?.startTime)}
                onChange={(name, value) => {
                  setScheduleTouched(true);
                  const schedule = {
                    ...(formValues.schedule || {
                      startTime: null,
                      stopTime: null,
                    }),
                    [name]: value.timeString || null,
                  };
                  changeHandler("schedule", schedule);
                }}
                extraLabel
                minimumHour={8}
                needConfirm={false}
                showColumnLabels
              />
            </div>
          ) : (
            <div className="w-1/2" />
          )}
          <div className="w-1/2">
            <TimePicker
              name="stopTime"
              placeholder="زمان توقف"
              value={getTimePickerValue(formValues.schedule?.stopTime)}
              onChange={(name, value) => {
                setScheduleTouched(true);
                const schedule = {
                  ...(formValues.schedule || {
                    startTime: null,
                    stopTime: null,
                  }),
                  [name]: value.timeString || null,
                };
                changeHandler("schedule", schedule);
              }}
              extraLabel
              minimumHour={8}
              needConfirm={false}
              showColumnLabels
            />
          </div>
        </div>
      </div>
      {editWarning && (
        <div
          role="alert"
          className="w-full whitespace-normal rounded border border-amber-300 bg-amber-50 p-2 text-right leading-6 text-amber-900"
        >
          {editWarning}
        </div>
      )}
      <div className="flex flex-row justify-between items-center w-full">
        <Button
          onClick={onCancelClick}
          text="لغو"
          variant="outlined"
          className="!w-16 !h-6 !text-xs"
        />
        <Button
          onClick={onSendClick}
          text="ارسال"
          variant="contained"
          className="!w-16 !h-6 !text-xs"
          disabled={loading || checkIsBtnDisabled()}
        />
      </div>
    </div>
  );

  if (failedOrders?.length) return <Modal title="سفارش‌های ناموفق" onClose={onFailedOrdersClose} UI={
    <div className="flex flex-col gap-3 text-xs whitespace-normal">
      {failedEditedCount != null ? (
        <div>{`${failedEditedCount} سفارش با موفقیت ویرایش شد.`}</div>
      ) : null}
      <div className="font-bold">سفارش‌های ناموفق</div>
      {failureSummary && <div>{failureSummary}</div>}
      <table className="w-full border-collapse border border-red-300 bg-red-50">
        <thead><tr><th>حساب</th><th>علت</th></tr></thead>
        <tbody>{failedOrders.map((row) => <tr key={row.stepOrderId}>
          <td>{row.accountName || row.stepOrderId}</td>
          <td>{row.reason}</td>
        </tr>)}</tbody>
      </table>
      <Button onClick={onFailedOrdersClose} text="بستن" variant="outlined" className="!w-16 !h-6 !text-xs" />
    </div>
  } />;
  return <Modal title="ویرایش سفارش" onClose={onCancelClick} UI={formUI} />;
};

export default EditOrder;
